// src/pages/Employee/EditEmployee.jsx
import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import employeeService from '../../services/employee.service';
import companyService from '../../services/company.service';
import Toast from '../../components/Toast/Toast';
import MultiSelect from '../../components/MultiSelect/MultiSelect';
import '../../styles/AddPages.css';

const REPORTING_HIERARCHY = {
  SUPER_ADMIN:   null,
  OFFICE_ADMIN:  null,
  ADMIN:         null,
  ZONAL_MANAGER: 'OFFICE_ADMIN',
  AREA_MANAGER:  'ZONAL_MANAGER',
  EXECUTIVE:     'AREA_MANAGER',
  SALES_OFFICER: 'EXECUTIVE',
  FIELD_AGENT:   'SALES_OFFICER',
  GODOWN:        'OFFICE_ADMIN',
  ACCOUNTS:      'OFFICE_ADMIN',
};

const ROLE_LABELS = {
  ADMIN: 'Admin', OFFICE_ADMIN: 'Office Admin', ZONAL_MANAGER: 'Zonal Manager',
  AREA_MANAGER: 'Area Manager', EXECUTIVE: 'Executive', SALES_OFFICER: 'Sales Officer',
  FIELD_AGENT: 'Field Agent', GODOWN: 'Godown Incharge', ACCOUNTS: 'Accounts',
};

const ROLES_REQUIRING_GODOWN  = ['ZONAL_MANAGER','AREA_MANAGER','EXECUTIVE','SALES_OFFICER','FIELD_AGENT','GODOWN'];
const ROLES_NEEDING_FULL_AREA = ['ZONAL_MANAGER','AREA_MANAGER','EXECUTIVE','SALES_OFFICER','FIELD_AGENT'];
const ROLES_GODOWN_ONLY       = ['GODOWN'];

let rowIdCounter = 0;
const nextRowId = () => `row-${++rowIdCounter}-${Date.now()}`;

const emptyRow = () => ({
  rowId: nextRowId(),
  companyId: '',
  godownId: '',
  selectedRegionIds: [],
  godownOptions: [],
  regionOptions: [],
  loadingGodowns: false,
  loadingRegions: false,
});

const EditEmployee = () => {
  const { id }   = useParams();
  const navigate = useNavigate();
  const { isSuperAdmin } = useAuth();

  const [formData, setFormData] = useState({
    employeeName: '', employeeIdentityCode: '', designationRole: '',
    reportingTo: '', regionId: '',
    headquarterIds: [], subHeadquarterIds: [], godownIds: [],
    email: '', mobile: '', DA: '', bikeTA: '', carTA: '',
  });

  const [roles, setRoles]                       = useState([]);
  const [reportingOptions, setReportingOptions] = useState([]);
  const [regions, setRegions]                   = useState([]);
  const [headquarters, setHeadquarters]         = useState([]);
  const [subHeadquarters, setSubHeadquarters]   = useState([]);
  const [godowns, setGodowns]                   = useState([]);

  // ── GODOWN role: multi company/godown/region assignment rows ─────────────
  const [companies, setCompanies] = useState([]);
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [godownAssignments, setGodownAssignments] = useState([emptyRow()]);

  const [loadingPage, setLoadingPage]         = useState(true);
  const [loadingManagers, setLoadingManagers] = useState(false);
  const [loadingRegions, setLoadingRegions]   = useState(false);
  const [loadingHQs, setLoadingHQs]           = useState(false);
  const [loadingSubHQs, setLoadingSubHQs]     = useState(false);
  const [loadingGodowns, setLoadingGodowns]   = useState(false);

  const [errors, setErrors]             = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast]               = useState(null);

  // ✅ Flags — true only when USER changes the field, false during initial load
  const roleChangedByUser   = useRef(false);
  const regionChangedByUser = useRef(false);
  const hqChangedByUser     = useRef(false);

  // ── Helpers ───────────────────────────────────────────────────────────────
  const setFallbackRoles = () => setRoles([
    { id: 1, name: 'OFFICE_ADMIN',  displayName: 'Office Admin',     level: 100 },
    { id: 2, name: 'ADMIN',         displayName: 'Admin',            level: 90  },
    { id: 3, name: 'ZONAL_MANAGER', displayName: 'Zonal Manager',    level: 70  },
    { id: 4, name: 'AREA_MANAGER',  displayName: 'Area Manager',     level: 60  },
    { id: 5, name: 'EXECUTIVE',     displayName: 'Executive',        level: 50  },
    { id: 6, name: 'SALES_OFFICER', displayName: 'Sales Officer',    level: 30  },
    { id: 7, name: 'ACCOUNTS',      displayName: 'Accounts',         level: 25  },
    { id: 8, name: 'FIELD_AGENT',   displayName: 'Field Agent',      level: 20  },
    { id: 9, name: 'GODOWN',        displayName: 'Go Down Incharge', level: 10  },
  ]);

  const loadHQs = (regionId) => {
    setLoadingHQs(true);
    employeeService.getHeadquartersByRegion(regionId)
      .then(res => { if (res.success && res.data) setHeadquarters(res.data); })
      .finally(() => setLoadingHQs(false));
  };

  const loadGodowns = (regionId) => {
    setLoadingGodowns(true);
    employeeService.getGodownsByRegion(regionId)
      .then(res => { if (res.success && res.data) setGodowns(res.data); })
      .finally(() => setLoadingGodowns(false));
  };

  const loadSubHQs = (hqIds) => {
    if (!hqIds || hqIds.length === 0) return;
    setLoadingSubHQs(true);
    Promise.all(
      hqIds.map(hqId => employeeService.getSubHeadquartersByHQ(hqId).then(r => r.success ? r.data : []))
    ).then(results => {
      const all = results.flat();
      const unique = Array.from(new Map(all.map(s => [s.id, s])).values());
      setSubHeadquarters(unique);
    }).finally(() => setLoadingSubHQs(false));
  };

  const fetchCompanies = async () => {
    setLoadingCompanies(true);
    try {
      const res = await companyService.getAllCompanies();
      if (res.success && res.data) setCompanies(res.data);
    } catch {
      setToast({ message: 'Failed to load companies', type: 'error' });
    } finally { setLoadingCompanies(false); }
  };

  // ── Godown assignment row handlers (shared: init-time + user interaction) ─

  const updateRow = (rowId, patch) => {
    setGodownAssignments(prev => prev.map(r => r.rowId === rowId ? { ...r, ...patch } : r));
  };

  const loadRowGodownOptions = async (rowId, companyId) => {
    if (!companyId) return;
    updateRow(rowId, { loadingGodowns: true });
    try {
      const res = await employeeService.getGodownsByCompany(companyId);
      updateRow(rowId, { godownOptions: res.success ? (res.data || []) : [] });
    } catch {
      setToast({ message: 'Failed to load godowns for this company', type: 'error' });
    } finally {
      updateRow(rowId, { loadingGodowns: false });
    }
  };

  const loadRowRegionOptions = async (rowId, godownId, companyId) => {
    if (!godownId || !companyId) return;
    updateRow(rowId, { loadingRegions: true });
    try {
      const res = await employeeService.getRegionsForGodown(godownId, companyId);
      updateRow(rowId, { regionOptions: res.success ? (res.data || []) : [] });
    } catch {
      setToast({ message: 'Failed to load regions for this godown', type: 'error' });
    } finally {
      updateRow(rowId, { loadingRegions: false });
    }
  };

  const handleRowCompanyChange = (rowId, companyId) => {
    updateRow(rowId, {
      companyId, godownId: '', selectedRegionIds: [],
      godownOptions: [], regionOptions: [],
    });
    if (errors.godownAssignments) setErrors(prev => ({ ...prev, godownAssignments: '' }));
    loadRowGodownOptions(rowId, companyId);
  };

  const handleRowGodownChange = (rowId, godownId, companyId) => {
    updateRow(rowId, { godownId, selectedRegionIds: [], regionOptions: [] });
    loadRowRegionOptions(rowId, godownId, companyId);
  };

  const handleRowRegionsChange = (rowId, regionIds) => {
    updateRow(rowId, { selectedRegionIds: regionIds });
    if (errors.godownAssignments) setErrors(prev => ({ ...prev, godownAssignments: '' }));
  };

  const addAssignmentRow = () => setGodownAssignments(prev => [...prev, emptyRow()]);

  const removeAssignmentRow = (rowId) => {
    setGodownAssignments(prev => {
      const next = prev.filter(r => r.rowId !== rowId);
      return next.length > 0 ? next : [emptyRow()];
    });
  };

  // ── Init: load employee + roles + regions + companies ────────────────────
  useEffect(() => {
    const init = async () => {
      try {
        const [empRes, rolesRes, regionsRes] = await Promise.all([
          employeeService.getEmployeeById(id),
          employeeService.getRoles(),
          employeeService.getAllRegions(),
        ]);

        if (rolesRes?.success && Array.isArray(rolesRes.data)) setRoles(rolesRes.data);
        else setFallbackRoles();

        if (regionsRes?.success && regionsRes.data) setRegions(regionsRes.data);

        if (empRes.success && empRes.data) {
          const e = empRes.data;
          const roleName = e.role?.name || '';

          const hqIds = Array.isArray(e.headquarterIds) && e.headquarterIds.length > 0
            ? e.headquarterIds.map(Number)
            : (e.headquarter?.id || e.headquarterId ? [Number(e.headquarter?.id || e.headquarterId)] : []);

          const subHqIds = Array.isArray(e.subHeadquarterIds) && e.subHeadquarterIds.length > 0
            ? e.subHeadquarterIds.map(Number)
            : (e.subHeadquarter?.id || e.subHeadquarterId ? [Number(e.subHeadquarter?.id || e.subHeadquarterId)] : []);

          const godownIds = Array.isArray(e.godownIds) && e.godownIds.length > 0
            ? e.godownIds.map(Number)
            : (e.godown?.id || e.godownId ? [Number(e.godown?.id || e.godownId)] : []);

          const resolvedRegionId = e.region?.id || e.regionId || '';

          setFormData({
            employeeName:         `${e.firstName || ''} ${e.lastName || ''}`.trim(),
            employeeIdentityCode: e.employeeCode || '',
            designationRole:      roleName,
            reportingTo:          e.reportingTo ? String(e.reportingTo) : '',
            regionId:             resolvedRegionId ? String(resolvedRegionId) : '',
            headquarterIds:       hqIds,
            subHeadquarterIds:    subHqIds,
            godownIds:            godownIds,
            email:  e.email  || '',
            mobile: e.phone  || '',
            DA:     e.DA     ? String(e.DA)     : '',
            bikeTA: e.bikeTA ? String(e.bikeTA) : '',
            carTA:  e.carTA  ? String(e.carTA)  : '',
          });

          // Pre-load dependent dropdowns
          const managerRole = REPORTING_HIERARCHY[roleName];
          if (managerRole) {
            setLoadingManagers(true);
            const companyId = isSuperAdmin ? localStorage.getItem('selectedCompanyId') : undefined;
            employeeService.getUsersByRole(managerRole, companyId)
              .then(res => { if (res.success && res.data) setReportingOptions(res.data); })
              .catch(() => {})
              .finally(() => setLoadingManagers(false));
          }

          if (ROLES_GODOWN_ONLY.includes(roleName)) {
            // Load company list, then rebuild assignment rows from existing godownAccess grants
            fetchCompanies();

            const grants = Array.isArray(e.godownAccess) ? e.godownAccess : [];
            if (grants.length > 0) {
              // Group grants by (godownId, companyId) → one row per group, regions merged
              const grouped = new Map();
              for (const g of grants) {
                const key = `${g.godownId}:${g.companyId}`;
                if (!grouped.has(key)) {
                  grouped.set(key, {
                    godownId: g.godownId, companyId: g.companyId, regionIds: [],
                  });
                }
                grouped.get(key).regionIds.push(g.regionId);
              }

              const rows = await Promise.all(
                Array.from(grouped.values()).map(async (group) => {
                  const row = emptyRow();
                  row.companyId = String(group.companyId);
                  row.godownId  = String(group.godownId);
                  row.selectedRegionIds = group.regionIds;

                  const [godownsRes, regionsForGodownRes] = await Promise.all([
                    employeeService.getGodownsByCompany(group.companyId),
                    employeeService.getRegionsForGodown(group.godownId, group.companyId),
                  ]);
                  row.godownOptions = godownsRes.success ? (godownsRes.data || []) : [];
                  row.regionOptions = regionsForGodownRes.success ? (regionsForGodownRes.data || []) : [];
                  return row;
                })
              );
              setGodownAssignments(rows.length > 0 ? rows : [emptyRow()]);
            } else {
              setGodownAssignments([emptyRow()]);
            }
          } else if (resolvedRegionId) {
            loadHQs(resolvedRegionId);
            loadGodowns(resolvedRegionId);
          }

          if (hqIds.length > 0) loadSubHQs(hqIds);
        }
      } catch {
        setToast({ message: 'Failed to load employee data', type: 'error' });
      } finally {
        setLoadingPage(false);
      }
    };

    init();
  }, [id, isSuperAdmin]);

  // ── Role change → reload managers + optionally reset area ─────────────────
  useEffect(() => {
    const role = formData.designationRole;
    if (!role) return;

    if (roleChangedByUser.current) {
      setFormData(prev => ({
        ...prev,
        reportingTo: '',
        regionId: '',
        headquarterIds: [],
        subHeadquarterIds: [],
        godownIds: [],
      }));
      setReportingOptions([]);
      setHeadquarters([]); setSubHeadquarters([]); setGodowns([]);
      setGodownAssignments([emptyRow()]);

      const managerRole = REPORTING_HIERARCHY[role];
      if (managerRole) {
        setLoadingManagers(true);
        const companyId = isSuperAdmin ? localStorage.getItem('selectedCompanyId') : undefined;
        employeeService.getUsersByRole(managerRole, companyId)
          .then(res => { if (res.success && res.data) setReportingOptions(res.data); })
          .catch(() => setToast({ message: 'Failed to load managers', type: 'error' }))
          .finally(() => setLoadingManagers(false));
      }

      if (ROLES_GODOWN_ONLY.includes(role)) {
        fetchCompanies();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.designationRole]);

  // ── Region change → reload HQs + Godowns (full-area roles only) ──────────
  useEffect(() => {
    if (ROLES_GODOWN_ONLY.includes(formData.designationRole)) return;

    if (regionChangedByUser.current) {
      setFormData(prev => ({ ...prev, headquarterIds: [], subHeadquarterIds: [], godownIds: [] }));
      setHeadquarters([]); setSubHeadquarters([]); setGodowns([]);
      if (!formData.regionId) return;
      loadHQs(formData.regionId);
      loadGodowns(formData.regionId);
    }
  }, [formData.regionId, formData.designationRole]);

  // ── HQ change → reload SubHQs ─────────────────────────────────────────────
  useEffect(() => {
    if (hqChangedByUser.current) {
      setFormData(prev => ({ ...prev, subHeadquarterIds: [] }));
      setSubHeadquarters([]);
      if (!formData.headquarterIds || formData.headquarterIds.length === 0) return;
      loadSubHQs(formData.headquarterIds);
    }
  }, [formData.headquarterIds]);

  // ── Input handlers ────────────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const e = {};
    if (!formData.employeeName.trim())         e.employeeName         = 'Required';
    if (!formData.employeeIdentityCode.trim()) e.employeeIdentityCode = 'Required';
    if (!formData.designationRole)             e.designationRole      = 'Required';
    const mgr = REPORTING_HIERARCHY[formData.designationRole];
    if (mgr && !formData.reportingTo)
      e.reportingTo = `Select the ${ROLE_LABELS[mgr]} this employee reports to`;
    if (!formData.email.trim()) e.email = 'Required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) e.email = 'Invalid email';
    if (!formData.mobile.trim()) e.mobile = 'Required';
    else if (!/^\d{10}$/.test(formData.mobile)) e.mobile = 'Must be 10 digits';

    if (ROLES_GODOWN_ONLY.includes(formData.designationRole)) {
      const validRows = godownAssignments.filter(r => r.companyId && r.godownId && r.selectedRegionIds.length > 0);
      if (validRows.length === 0) {
        e.godownAssignments = 'Add at least one complete company → godown → region assignment';
      }
    } else if (ROLES_REQUIRING_GODOWN.includes(formData.designationRole) &&
        (!formData.godownIds || formData.godownIds.length === 0)) {
      e.godownIds = 'Please select at least one Godown';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const parts     = formData.employeeName.trim().split(' ');
      const firstName = parts[0] || '';
      const lastName  = parts.slice(1).join(' ') || parts[0];
      const selRole   = roles.find(r => r.name === formData.designationRole);
      if (!selRole) {
        setToast({ message: 'Select a valid role', type: 'error' });
        setIsSubmitting(false);
        return;
      }

      const isGodownRole = ROLES_GODOWN_ONLY.includes(formData.designationRole);

      const godownAccess = isGodownRole
        ? godownAssignments
            .filter(r => r.companyId && r.godownId && r.selectedRegionIds.length > 0)
            .flatMap(r => r.selectedRegionIds.map(regionId => ({
              godownId: parseInt(r.godownId),
              regionId: parseInt(regionId),
            })))
        : [];

      const legacyGodownIds = isGodownRole
        ? [...new Set(godownAccess.map(g => g.godownId))]
        : formData.godownIds;

      const payload = {
        firstName, lastName,
        roleId:       selRole.id,
        employeeCode: formData.employeeIdentityCode,
        email:        formData.email,
        phone:        formData.mobile || undefined,
        DA:           formData.DA     ? parseFloat(formData.DA)     : undefined,
        bikeTA:       formData.bikeTA ? parseFloat(formData.bikeTA) : undefined,
        carTA:        formData.carTA  ? parseFloat(formData.carTA)  : undefined,
        reportingTo:  formData.reportingTo ? parseInt(formData.reportingTo) : undefined,
        regionId:     formData.regionId    ? parseInt(formData.regionId)    : undefined,
        headquarterIds:    formData.headquarterIds?.length    > 0 ? formData.headquarterIds    : undefined,
        subHeadquarterIds: formData.subHeadquarterIds?.length > 0 ? formData.subHeadquarterIds : undefined,
        godownIds:         legacyGodownIds?.length             > 0 ? legacyGodownIds           : undefined,
        godownAccess:      isGodownRole ? godownAccess : undefined,  // ✅ always send (even []) so backend can clear stale grants on save
      };

      const res = await employeeService.updateEmployee(id, payload);
      if (res.success) {
        setToast({ message: 'Employee updated successfully!', type: 'success' });
        setTimeout(() => navigate('/employee/view'), 1500);
      }
    } catch (err) {
      setToast({ message: err.response?.data?.message || 'Failed to update employee', type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loadingPage) return <div className="add-employee-page"><p style={{ padding: 40 }}>Loading...</p></div>;

  const role           = formData.designationRole;
  const mgr            = REPORTING_HIERARCHY[role];
  const managerLabel   = mgr ? ROLE_LABELS[mgr] || '' : '';
  const showReporting  = role && mgr !== null && mgr !== undefined;
  const showFullArea   = ROLES_NEEDING_FULL_AREA.includes(role);
  const showGodownOnly = ROLES_GODOWN_ONLY.includes(role);
  const godownRequired = ROLES_REQUIRING_GODOWN.includes(role);

  return (
    <div className="add-employee-page">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      <nav aria-label="breadcrumb">
        <ol className="breadcrumb">
          <li className="breadcrumb-item"><a href="/dashboard/overview">Dashboard</a></li>
          <li className="breadcrumb-item"><a href="/employee/view">Employees</a></li>
          <li className="breadcrumb-item active">Edit Employee</li>
        </ol>
      </nav>

      <div className="page-header"><h4 className="page-title">Edit Employee</h4></div>

      <div className="form-card">
        <form onSubmit={handleSubmit}>
          {/* Role & Identity */}
          <div className="form-section">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Employee Name <span className="required">*</span></label>
                <input type="text" name="employeeName"
                  className={`form-input ${errors.employeeName?'error':''}`}
                  value={formData.employeeName} onChange={handleChange} placeholder="Enter full name"/>
                {errors.employeeName && <span className="error-message">{errors.employeeName}</span>}
              </div>
              <div className="form-group">
                <label className="form-label">Employee Identity Code <span className="required">*</span></label>
                <input type="text" name="employeeIdentityCode"
                  className={`form-input ${errors.employeeIdentityCode?'error':''}`}
                  value={formData.employeeIdentityCode} onChange={handleChange} placeholder="Enter employee code"/>
                {errors.employeeIdentityCode && <span className="error-message">{errors.employeeIdentityCode}</span>}
              </div>
              <div className="form-group">
                <label className="form-label">Designation Role <span className="required">*</span></label>
                <select
                  name="designationRole"
                  className={`form-input ${errors.designationRole?'error':''}`}
                  value={formData.designationRole}
                  onChange={(e) => { roleChangedByUser.current = true; handleChange(e); }}
                >
                  <option value="">Select Role</option>
                  {roles.map(r => <option key={r.id} value={r.name}>{r.displayName||r.name}</option>)}
                </select>
                {errors.designationRole && <span className="error-message">{errors.designationRole}</span>}
              </div>
            </div>

            {showReporting && (
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Reporting To ({managerLabel}) <span className="required">*</span></label>
                  {loadingManagers ? (
                    <div className="form-input" style={{color:'#adb5bd',cursor:'default'}}>Loading...</div>
                  ) : reportingOptions.length === 0 ? (
                    <select className="form-input" disabled><option>No {managerLabel} found</option></select>
                  ) : (
                    <select name="reportingTo"
                      className={`form-input ${errors.reportingTo?'error':''}`}
                      value={formData.reportingTo} onChange={handleChange}>
                      <option value="">Select {managerLabel}</option>
                      {reportingOptions.map(emp => (
                        <option key={emp.id} value={emp.id}>
                          {emp.firstName} {emp.lastName}{emp.employeeCode?` (${emp.employeeCode})`:''}
                        </option>
                      ))}
                    </select>
                  )}
                  {errors.reportingTo && <span className="error-message">{errors.reportingTo}</span>}
                </div>
              </div>
            )}
          </div>

          {/* Area Assignment — Full — UNCHANGED */}
          {showFullArea && (
            <div className="form-section">
              <h3 style={{color:'#4caf50',fontSize:13,fontWeight:700,marginBottom:16,textTransform:'uppercase',letterSpacing:'0.5px'}}>
                📍 Area Assignment
              </h3>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">Region</label>
                  {loadingRegions ? (
                    <div className="form-input" style={{color:'#adb5bd',cursor:'default'}}>Loading regions...</div>
                  ) : (
                    <select
                      name="regionId" className="form-input"
                      value={formData.regionId}
                      onChange={(e) => { regionChangedByUser.current = true; handleChange(e); }}
                    >
                      <option value="">Select Region</option>
                      {regions.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                    </select>
                  )}
                </div>
                <MultiSelect label="Headquarters" options={headquarters} selected={formData.headquarterIds}
                  onChange={(ids) => {
                    hqChangedByUser.current = true;
                    setFormData(prev => ({ ...prev, headquarterIds: ids }));
                  }}
                  disabled={!formData.regionId} loading={loadingHQs}
                  placeholder={!formData.regionId ? 'Select Region first' : 'Select Headquarters'}
                  emptyMsg="No headquarters in this region"/>
                <MultiSelect label="Sub-Headquarters" options={subHeadquarters} selected={formData.subHeadquarterIds}
                  onChange={(ids) => setFormData(prev => ({ ...prev, subHeadquarterIds: ids }))}
                  disabled={!formData.headquarterIds||formData.headquarterIds.length===0} loading={loadingSubHQs}
                  placeholder={!formData.headquarterIds?.length ? 'Select HQ first' : 'Select Sub-Headquarters'}
                  emptyMsg="No sub-headquarters for selected HQs"/>
              </div>
              <div className="form-row">
                <MultiSelect
                  label={<span>Godowns {godownRequired ? <span className="required"> *</span> : <span style={{fontWeight:400,color:'#adb5bd'}}> (Optional)</span>}</span>}
                  options={godowns} selected={formData.godownIds}
                  onChange={(ids) => {
                    setFormData(prev => ({ ...prev, godownIds: ids }));
                    if (errors.godownIds) setErrors(prev => ({ ...prev, godownIds: '' }));
                  }}
                  disabled={!formData.regionId} loading={loadingGodowns}
                  placeholder={!formData.regionId ? 'Select Region first' : 'Select Godowns'}
                  emptyMsg="No godowns in this region"
                />
                {errors.godownIds && <span className="error-message">{errors.godownIds}</span>}
              </div>
            </div>
          )}

          {/* Area Assignment — Godown role: company → godown → region rows */}
          {showGodownOnly && (
            <div className="form-section">
              <h3 style={{color:'#4caf50',fontSize:13,fontWeight:700,marginBottom:16,textTransform:'uppercase',letterSpacing:'0.5px'}}>
                🏬 Godown Assignment
              </h3>
              <p style={{fontSize:13, color:'#6c757d', margin:'-10px 0 16px 0'}}>
                Godown users can be assigned to multiple companies. For each company, pick which godown they'll manage and which of that company's regions they can see orders for.
              </p>

              {godownAssignments.map((row, idx) => (
                <div key={row.rowId} style={{
                  border: '1px solid #e9ecef', borderRadius: 10, padding: 16,
                  marginBottom: 12, background: '#fafbfc', position: 'relative',
                }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 12 }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: '#495057' }}>Assignment {idx + 1}</span>
                    {godownAssignments.length > 1 && (
                      <button type="button" onClick={() => removeAssignmentRow(row.rowId)}
                        style={{ background:'none', border:'none', color:'#e53935', fontSize:12, cursor:'pointer', fontWeight:600 }}>
                        ✕ Remove
                      </button>
                    )}
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Company <span className="required">*</span></label>
                      {loadingCompanies ? (
                        <div className="form-input" style={{color:'#adb5bd',cursor:'default'}}>Loading companies...</div>
                      ) : (
                        <select className="form-input" value={row.companyId}
                          onChange={(e) => handleRowCompanyChange(row.rowId, e.target.value)}>
                          <option value="">Select Company</option>
                          {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                      )}
                    </div>

                    <div className="form-group">
                      <label className="form-label">Godown <span className="required">*</span></label>
                      {row.loadingGodowns ? (
                        <div className="form-input" style={{color:'#adb5bd',cursor:'default'}}>Loading godowns...</div>
                      ) : (
                        <select className="form-input" value={row.godownId} disabled={!row.companyId}
                          onChange={(e) => handleRowGodownChange(row.rowId, e.target.value, row.companyId)}>
                          <option value="">{!row.companyId ? 'Select Company first' : row.godownOptions.length === 0 ? 'No godowns for this company' : 'Select Godown'}</option>
                          {row.godownOptions.map(g => <option key={g.id} value={g.id}>{g.name} — {g.location}</option>)}
                        </select>
                      )}
                    </div>
                  </div>

                  <div className="form-row">
                    <MultiSelect
                      label={<span>Regions <span className="required">*</span></span>}
                      options={row.regionOptions}
                      selected={row.selectedRegionIds}
                      onChange={(ids) => handleRowRegionsChange(row.rowId, ids)}
                      disabled={!row.godownId}
                      loading={row.loadingRegions}
                      placeholder={!row.godownId ? 'Select Godown first' : 'Select Regions'}
                      emptyMsg="This godown has no regions linked for this company"
                    />
                  </div>
                </div>
              ))}

              {errors.godownAssignments && (
                <span className="error-message" style={{ display:'block', marginBottom: 8 }}>
                  {errors.godownAssignments}
                </span>
              )}

              <button type="button" onClick={addAssignmentRow}
                style={{
                  background:'#e8f5e9', color:'#2e7d32', border:'1px dashed #2e7d32',
                  borderRadius: 8, padding: '8px 16px', fontSize: 13, fontWeight: 600, cursor:'pointer',
                }}>
                + Add another company / godown assignment
              </button>
            </div>
          )}

          {/* Contact */}
          <div className="form-section">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Email <span className="required">*</span></label>
                <input type="email" name="email"
                  className={`form-input ${errors.email?'error':''}`}
                  value={formData.email} onChange={handleChange} placeholder="Enter email"/>
                {errors.email && <span className="error-message">{errors.email}</span>}
              </div>
              <div className="form-group">
                <label className="form-label">Mobile <span className="required">*</span></label>
                <input type="tel" name="mobile" maxLength="10"
                  className={`form-input ${errors.mobile?'error':''}`}
                  value={formData.mobile} onChange={handleChange} placeholder="10-digit mobile"/>
                {errors.mobile && <span className="error-message">{errors.mobile}</span>}
              </div>
            </div>
          </div>

          {/* Allowances */}
          <div className="form-section">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">DA</label>
                <input type="number" name="DA" className="form-input" value={formData.DA}
                  onChange={handleChange} placeholder="Dearness Allowance" step="0.01"/>
              </div>
              <div className="form-group">
                <label className="form-label">Bike TA</label>
                <input type="number" name="bikeTA" className="form-input" value={formData.bikeTA}
                  onChange={handleChange} placeholder="Bike Travel Allowance" step="0.01"/>
              </div>
              <div className="form-group">
                <label className="form-label">Car TA</label>
                <input type="number" name="carTA" className="form-input" value={formData.carTA}
                  onChange={handleChange} placeholder="Car Travel Allowance" step="0.01"/>
              </div>
            </div>
          </div>

          <div className="form-actions">
            <button type="button" className="btn btn-secondary"
              onClick={() => navigate('/employee/view')} disabled={isSubmitting}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditEmployee;